import { CognitoUserPool, CognitoUserAttribute, ICognitoUserData, CognitoUser } from "amazon-cognito-identity-js";
import { CognitoIdentityProviderClient, InitiateAuthCommand, AdminConfirmSignUpCommand, SignUpCommand, ResendConfirmationCodeCommand, CodeDeliveryDetailsType, AuthFlowType, InitiateAuthCommandOutput } from "@aws-sdk/client-cognito-identity-provider";
import { AuthenticationResultType } from "aws-sdk/clients/cognitoidentityserviceprovider";
import TagManager from 'react-gtm-module';
import * as SignatureService from "../services/SignatureService";
import * as Globals from '../library/Globals';
import * as Errors from "../library/Errors";
import UserAccount from "../classes/UserAccount";
import axiosInstance from "../services/AxiosService";

const region = process.env.REACT_APP_REGION;
const accessKeyId = process.env.REACT_APP_COGNITO_ACCESS_KEY || '';
const secretAccessKey = process.env.REACT_APP_SECRET_ACCESS_KEY || '';

export const userPool1 = undefined as unknown as CognitoUserPool; 

export const userPool2 = undefined as unknown as CognitoUserPool; 

const getUserPool = (userPoolId: SignatureService.UserPoolId) => {
    if (userPoolId === 'Main') return userPool1;
    return userPool2;
}

export const signInUser = async (email: string, password: string, userPoolId: string) => {
    return signInCognito(userPoolId, email, password);
}

export const signInUserPool1 = async (email: string, password: string) => {
    var authenticationResult = await signInCognito('Main', email, password);
    return authenticationResult;
}

export const signInUserPool2 = async (email: string, password: string) => {
    var authenticationResult = await signInCognito('PWA', email, password);
    return authenticationResult;
}

const signInCognito = (
  userPool: string,
  email: string,
  password: string
): Promise<AuthenticationResultType> => {
  return new Promise(async (resolve, reject) => {
    try {
      const res = await axiosInstance.post<InitiateAuthCommandOutput>(
        `${process.env.REACT_APP_SECURE_API_GATEWAY}/auth/login`,
        {
          username: email,
          password: password,
          userPool: userPool
        }
      );

      if(!res.data.AuthenticationResult) {
        return reject(new Error('AuthenticationResult is missing in the response'));
      }

      resolve(res.data.AuthenticationResult);
    } catch (err) {
      reject(err);
    }
  });
};


export const createUser2 = async ({
    email,
    password,
    firstName,
    lastName,
    country,
    fittingLocation: locationName,
    shipToNumber,
    locale,
}: UserAccount) => {
    email = email.toLowerCase();

    const poolCheck = await SignatureService.poolCheck(email);
    if (poolCheck.UserPool === "Main") {
        throw new Errors.UsernameTakenError("Username Taken Error");
    }

    try {
        await axiosInstance.post(
            `${process.env.REACT_APP_SECURE_API_GATEWAY}/auth/new-user`,
            {
                username: email,
                password: password,
                firstName: firstName,
                lastName: lastName,
                country: country,
                locationName: locationName,
                shipToNumber: shipToNumber,
                userPool: 'PWA'
            }
        );
    }
    catch (err) {
        TagManager.dataLayer({
            dataLayer: {
                event: 'log_error',
                description: 'Cognito Service - Create new user',
                msg: 'catch: ' + err
            }
        });
        throw (err);
    }
}

export const autoVerifyUser2 = async (userName: string) => {
    try {
        const accessKeyId = process.env.REACT_APP_COGNITO_ACCESS_KEY || "";
        const secretAccessKey = process.env.REACT_APP_SECRET_ACCESS_KEY || "";
        const region = process.env.REACT_APP_REGION || "";

        var creds = { accessKeyId: accessKeyId, secretAccessKey: secretAccessKey };
        const client = new CognitoIdentityProviderClient({ credentials: creds, region: region });
        const input = { UserPoolId: userPool2.getUserPoolId(), Username: userName }

        const command = new AdminConfirmSignUpCommand(input);
        const response = await client.send(command);
        return response;
    }
    catch (err) {
        TagManager.dataLayer({
            dataLayer: {
                event: 'log_error',
                description: 'Cognito Service - Confirm sign up',
                msg: 'catch: ' + err
            }
        });
    }
}

export const forgotPassword = (userName: string, userPoolId: SignatureService.UserPoolId): Promise<boolean> => {
    return new Promise<boolean>(async (resolve, reject) => {
        try {
            await axiosInstance.post(
                `${process.env.REACT_APP_SECURE_API_GATEWAY}/auth/forgot-password`,
                {
                    username: userName.toLowerCase(),
                    userPool: userPoolId
                }
            );
            resolve(true);
        } catch (err) {
            reject(err);
        }
    });
}

export const confirmNewPassword = (verificationCode: string, userName: string, newPassword: string, userPoolId: SignatureService.UserPoolId) => {
    return new Promise<boolean>(async (resolve, reject) => {
        try {
            await axiosInstance.post(
                `${process.env.REACT_APP_SECURE_API_GATEWAY}/auth/confirm-new-password`,
                {
                    username: userName.toLowerCase(),
                    confirmationCode: verificationCode,
                    newPassword: newPassword,
                    userPool: userPoolId
                }
            );
            resolve(true);
        } catch (err) {
            reject(err);
        }
    });
}

export const refreshAuthToken = async (refreshToken: string, userPoolNum: number) => {
    const userPoolId = userPoolNum === 1 ? 'Main' : 'PWA';
    
    var authenticationResult: AuthenticationResultType | null = null;
    try {
        const response = await axiosInstance.post(
            `${process.env.REACT_APP_SECURE_API_GATEWAY}/auth/refresh`,
            {
                refreshToken: refreshToken,
                userPool: userPoolId
            }
        );
        authenticationResult = response.data.AuthenticationResult;
    }
    catch (err: any) {
        TagManager.dataLayer({
            dataLayer: {
                event: 'log_error',
                description: 'Cognito Service - refresh auth token',
                msg: 'catch: ' + err
            }
        });
        //if user is not offline, then something went wrong with tokens. Force logging in again
        if (!Globals.errMsgIsOffline(err.message)){            
            (window as any).location.href = "/apps/wedgefitting";
        }
    }
    return authenticationResult;
}

export const resendEmailConfirmationCode = (email: string, userPoolId: SignatureService.UserPoolId): Promise<CodeDeliveryDetailsType> => {
    return new Promise(async (resolve, reject) => {
        try {
            const response = await axiosInstance.post(
                `${process.env.REACT_APP_SECURE_API_GATEWAY}/auth/resend-confirmation`,
                {
                    username: email.toLowerCase(),
                    userPool: userPoolId
                }
            );
            resolve(response.data);
        } catch (err: any) {
            TagManager.dataLayer({
                dataLayer: {
                    event: 'log_error',
                    description: 'Cognito Service - resend confirmation code',
                    msg: 'catch: ' + err
                }
            });
            reject(err);
        }
    });
}
